import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, FormControl, FormLabel, Input, Select, Textarea,
  SimpleGrid, VStack, HStack, Text, Divider, useToast, Badge, Tabs, TabList,
  Tab, TabPanels, TabPanel, Checkbox, NumberInput, NumberInputField,
  Image, Box, IconButton, Link, Wrap, WrapItem,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { MdUpload, MdImage, MdPictureAsPdf } from 'react-icons/md';
import type { Detencion, OficialSeguridad, UnidadPolicial, TipoDetencion } from './juzgadoTypes';
import juzgadoApi from '../../services/juzgadoApi';
import MapSelector from '../../components/MapSelector';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  mode: 'new' | 'edit' | 'view';
  detencion: Detencion | null;
  onGuardado: (d: Detencion) => void;
}

const TIPOS_DETENCION: TipoDetencion[] = [
  'Falta administrativa cívica',
  'Infracción de tránsito',
  'Alteración del orden',
  'Flagrancia delictiva',
  'Violencia',
  'Persona en situación de riesgo o protección',
  'Falta administrativa',
  'Otro',
];

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const ANIO_ACTUAL = new Date().getFullYear();

const EMPTY: Partial<Detencion> = {
  nombres: '',
  apellidoPaterno: '',
  apellidoMaterno: '',
  sexo: 'MASCULINO',
  tipoDetencion: 'Falta administrativa cívica',
  estatusDetenido: 'Puesto a disposición',
  estatus: 'Activa',
};

export default function ModalDetencion({ isOpen, onClose, mode, detencion, onGuardado }: Props) {
  const [form, setForm] = useState<Partial<Detencion>>(EMPTY);
  const [oficiales, setOficiales] = useState<OficialSeguridad[]>([]);
  const [unidades, setUnidades] = useState<UnidadPolicial[]>([]);
  const [saving, setSaving] = useState(false);
  const [archivos, setArchivos] = useState<any[]>([]);
  const [subiendo, setSubiendo] = useState(false);
  const refFrontal = useRef<HTMLInputElement>(null);
  const refPerfil = useRef<HTMLInputElement>(null);
  const refIdent = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const readOnly = mode === 'view';

  useEffect(() => {
    if (isOpen) {
      setForm(detencion ? { ...detencion } : { ...EMPTY });
      setArchivos([]);
      if (!readOnly) {
        juzgadoApi.listarOficiales().then(setOficiales).catch(() => {});
        juzgadoApi.listarUnidades().then(setUnidades).catch(() => {});
      }
      if (detencion?.id) {
        juzgadoApi.listarArchivosDetencion(detencion.id).then(setArchivos).catch(() => {});
      }
    }
  }, [isOpen, detencion, readOnly]);

  const subirArchivo = async (file: File, tipo: string) => {
    if (!detencion?.id) {
      return toast({ title: 'Guarde primero la detención', description: 'Las fotos se suben después de registrar.', status: 'warning' });
    }
    setSubiendo(true);
    try {
      await juzgadoApi.subirArchivosDetencionTipo(detencion.id, [file], tipo);
      const lista = await juzgadoApi.listarArchivosDetencion(detencion.id);
      setArchivos(lista);
      toast({ title: 'Archivo subido', status: 'success' });
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, status: 'error' });
    } finally {
      setSubiendo(false);
    }
  };

  const archivosPorTipo = (tipo: string) => archivos.filter(a => a.tipo === tipo);

  const set = (k: keyof Detencion, v: any) => setForm(prev => ({ ...prev, [k]: v }));

  const calcularEdad = (dia?: number, mes?: number, anio?: number): number | undefined => {
    if (!dia || !mes || !anio) return undefined;
    const hoy = new Date();
    const nacimiento = new Date(anio, mes - 1, dia);
    let edad = hoy.getFullYear() - nacimiento.getFullYear();
    const m = hoy.getMonth() - nacimiento.getMonth();
    if (m < 0 || (m === 0 && hoy.getDate() < dia)) edad--;
    return edad >= 0 ? edad : undefined;
  };

  const handleFechaNac = (campo: 'fechaNacDia' | 'fechaNacMes' | 'fechaNacAnio', val: number) => {
    const next = { ...form, [campo]: val };
    const edad = calcularEdad(
      campo === 'fechaNacDia' ? val : form.fechaNacDia,
      campo === 'fechaNacMes' ? val : form.fechaNacMes,
      campo === 'fechaNacAnio' ? val : form.fechaNacAnio,
    );
    setForm({ ...next, edadAlRegistro: edad ?? form.edadAlRegistro });
  };

  const handleGuardar = async () => {
    if (!form.nombres?.trim()) return toast({ title: 'Nombre requerido', status: 'warning' });
    if (!form.apellidoPaterno?.trim()) return toast({ title: 'Apellido paterno requerido', status: 'warning' });
    if (!form.tipoDetencion) return toast({ title: 'Tipo de detención requerido', status: 'warning' });

    setSaving(true);
    try {
      let resultado: Detencion;
      if (mode === 'new') {
        resultado = await juzgadoApi.crearDetencion(form);
        toast({ title: `Detención registrada: ${resultado.folio}`, status: 'success' });
      } else {
        resultado = await juzgadoApi.actualizarDetencion(detencion!.id, form);
        toast({ title: 'Detención actualizada', status: 'success' });
      }
      onGuardado(resultado);
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, status: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const titulo = mode === 'new' ? 'Nueva Detención' : mode === 'edit' ? 'Editar Detención' : `Detención: ${detencion?.folio}`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg="#5b182b" color="white" borderTopRadius="md">
          <HStack>
            <Text>{titulo}</Text>
            {detencion?.folio && <Badge colorScheme="yellow">{detencion.folio}</Badge>}
          </HStack>
        </ModalHeader>
        <ModalCloseButton color="white" />

        <ModalBody pt={4}>
          <Tabs colorScheme="red" variant="enclosed">
            <TabList>
              <Tab _selected={{ color: '#5b182b', borderColor: '#5b182b' }}>Datos Personales</Tab>
              <Tab _selected={{ color: '#5b182b', borderColor: '#5b182b' }}>Datos del Hecho</Tab>
              <Tab _selected={{ color: '#5b182b', borderColor: '#5b182b' }}>Evidencia y Ubicación</Tab>
            </TabList>

            <TabPanels>
              {/* ── TAB 1: Datos personales ──────────────────────────── */}
              <TabPanel px={0}>
                <VStack spacing={4} align="stretch">
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Nombre(s)</FormLabel>
                      <Input
                        value={form.nombres ?? ''}
                        onChange={e => set('nombres', e.target.value.toUpperCase())}
                        isReadOnly={readOnly}
                        borderColor="#bb8c52"
                        _focus={{ borderColor: '#5b182b' }}
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Apellido Paterno</FormLabel>
                      <Input
                        value={form.apellidoPaterno ?? ''}
                        onChange={e => set('apellidoPaterno', e.target.value.toUpperCase())}
                        isReadOnly={readOnly}
                        borderColor="#bb8c52"
                        _focus={{ borderColor: '#5b182b' }}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Apellido Materno</FormLabel>
                      <Input
                        value={form.apellidoMaterno ?? ''}
                        onChange={e => set('apellidoMaterno', e.target.value.toUpperCase())}
                        isReadOnly={readOnly}
                        borderColor="#bb8c52"
                        _focus={{ borderColor: '#5b182b' }}
                      />
                    </FormControl>
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                    <FormControl>
                      <FormLabel fontSize="sm">Alias</FormLabel>
                      <Input value={form.alias ?? ''} onChange={e => set('alias', e.target.value)} isReadOnly={readOnly} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">CURP</FormLabel>
                      <Input value={form.curp ?? ''} onChange={e => set('curp', e.target.value.toUpperCase())} isReadOnly={readOnly} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} maxLength={18} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">RFC</FormLabel>
                      <Input value={form.rfc ?? ''} onChange={e => set('rfc', e.target.value.toUpperCase())} isReadOnly={readOnly} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} maxLength={13} />
                    </FormControl>
                  </SimpleGrid>

                  <Divider />
                  <Text fontWeight="semibold" color="#5b182b">Fecha de Nacimiento</Text>

                  <SimpleGrid columns={{ base: 1, md: 4 }} spacing={3}>
                    <FormControl>
                      <FormLabel fontSize="sm">Día</FormLabel>
                      <Select value={form.fechaNacDia ?? ''} onChange={e => handleFechaNac('fechaNacDia', Number(e.target.value))} isDisabled={readOnly} borderColor="#bb8c52">
                        <option value="">—</option>
                        {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                          <option key={d} value={d}>{String(d).padStart(2, '0')}</option>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Mes</FormLabel>
                      <Select value={form.fechaNacMes ?? ''} onChange={e => handleFechaNac('fechaNacMes', Number(e.target.value))} isDisabled={readOnly} borderColor="#bb8c52">
                        <option value="">—</option>
                        {MESES.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Año</FormLabel>
                      <Select value={form.fechaNacAnio ?? ''} onChange={e => handleFechaNac('fechaNacAnio', Number(e.target.value))} isDisabled={readOnly} borderColor="#bb8c52">
                        <option value="">—</option>
                        {Array.from({ length: ANIO_ACTUAL - 1900 + 1 }, (_, i) => ANIO_ACTUAL - i).map(a => (
                          <option key={a} value={a}>{a}</option>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Edad al registro</FormLabel>
                      <NumberInput value={form.edadAlRegistro ?? ''} min={0} max={130} isReadOnly={readOnly}>
                        <NumberInputField borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} onChange={e => set('edadAlRegistro', Number(e.target.value))} />
                      </NumberInput>
                    </FormControl>
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Sexo</FormLabel>
                      <Select value={form.sexo ?? ''} onChange={e => set('sexo', e.target.value)} isDisabled={readOnly} borderColor="#bb8c52">
                        <option value="MASCULINO">MASCULINO</option>
                        <option value="FEMENINO">FEMENINO</option>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Nacionalidad</FormLabel>
                      <Input value={form.nacionalidad ?? ''} onChange={e => set('nacionalidad', e.target.value)} isReadOnly={readOnly} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Estado de procedencia</FormLabel>
                      <Input value={form.estadoProcedencia ?? ''} onChange={e => set('estadoProcedencia', e.target.value)} isReadOnly={readOnly} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                    </FormControl>
                  </SimpleGrid>

                  <FormControl>
                    <FormLabel fontSize="sm">Domicilio</FormLabel>
                    <Textarea value={form.domicilio ?? ''} onChange={e => set('domicilio', e.target.value)} isReadOnly={readOnly} rows={2} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                  </FormControl>

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                    <FormControl>
                      <FormLabel fontSize="sm">Tipo de identificación</FormLabel>
                      <Select value={form.tipoIdentificacion ?? ''} onChange={e => set('tipoIdentificacion', e.target.value)} isDisabled={readOnly} borderColor="#bb8c52">
                        <option value="">Sin identificación</option>
                        <option value="INE">INE</option>
                        <option value="Pasaporte">Pasaporte</option>
                        <option value="Otro">Otro</option>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Número de identificación</FormLabel>
                      <Input value={form.numIdentificacion ?? ''} onChange={e => set('numIdentificacion', e.target.value)} isReadOnly={readOnly} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                    </FormControl>
                  </SimpleGrid>
                </VStack>
              </TabPanel>

              {/* ── TAB 2: Datos del hecho ────────────────────────────── */}
              <TabPanel px={0}>
                <VStack spacing={4} align="stretch">
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                    <FormControl>
                      <FormLabel fontSize="sm">Fecha del hecho</FormLabel>
                      <Input
                        type="date"
                        value={form.fechaHecho ? form.fechaHecho.substring(0, 10) : ''}
                        onChange={e => set('fechaHecho', e.target.value)}
                        isReadOnly={readOnly}
                        borderColor="#bb8c52"
                        _focus={{ borderColor: '#5b182b' }}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Hora del hecho</FormLabel>
                      <Input
                        type="time"
                        value={form.horaHecho ?? ''}
                        onChange={e => set('horaHecho', e.target.value)}
                        isReadOnly={readOnly}
                        borderColor="#bb8c52"
                        _focus={{ borderColor: '#5b182b' }}
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Tipo de detención</FormLabel>
                      <Select value={form.tipoDetencion ?? ''} onChange={e => set('tipoDetencion', e.target.value)} isDisabled={readOnly} borderColor="#bb8c52">
                        {TIPOS_DETENCION.map(t => <option key={t} value={t}>{t}</option>)}
                      </Select>
                    </FormControl>
                  </SimpleGrid>

                  {form.tipoDetencion === 'Otro' && (
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Especificar tipo</FormLabel>
                      <Input value={form.tipoDetencionOtro ?? ''} onChange={e => set('tipoDetencionOtro', e.target.value)} isReadOnly={readOnly} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                    </FormControl>
                  )}

                  <FormControl>
                    <FormLabel fontSize="sm">Motivo principal</FormLabel>
                    <Textarea value={form.motivoPrincipal ?? ''} onChange={e => set('motivoPrincipal', e.target.value)} isReadOnly={readOnly} rows={3} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm">Lugar exacto de detención</FormLabel>
                    <Input value={form.lugarDetencion ?? ''} onChange={e => set('lugarDetencion', e.target.value)} isReadOnly={readOnly} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm">Descripción detallada del hecho</FormLabel>
                    <Textarea value={form.descripcionHecho ?? ''} onChange={e => set('descripcionHecho', e.target.value)} isReadOnly={readOnly} rows={4} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                  </FormControl>

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                    <FormControl>
                      <FormLabel fontSize="sm">Oficial de seguridad</FormLabel>
                      <Select value={form.oficialId ?? ''} onChange={e => set('oficialId', e.target.value ? Number(e.target.value) : undefined)} isDisabled={readOnly} borderColor="#bb8c52">
                        <option value="">Sin asignar</option>
                        {oficiales.map(o => <option key={o.id} value={o.id}>{o.nombre} {o.credencial ? `(${o.credencial})` : ''}</option>)}
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Unidad policial</FormLabel>
                      <Select value={form.unidadId ?? ''} onChange={e => set('unidadId', e.target.value ? Number(e.target.value) : undefined)} isDisabled={readOnly} borderColor="#bb8c52">
                        <option value="">Sin asignar</option>
                        {unidades.map(u => <option key={u.id} value={u.id}>{u.numero} {u.descripcion ? `- ${u.descripcion}` : ''}</option>)}
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Duración de retención</FormLabel>
                      <Input value={form.duracionRetencion ?? ''} onChange={e => set('duracionRetencion', e.target.value)} isReadOnly={readOnly} placeholder="ej. 5 días" borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                    </FormControl>
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                    <FormControl>
                      <FormLabel fontSize="sm">Estatus del detenido</FormLabel>
                      <Select value={form.estatusDetenido ?? ''} onChange={e => set('estatusDetenido', e.target.value as any)} isDisabled={readOnly} borderColor="#bb8c52">
                        <option value="Puesto a disposición">Puesto a disposición</option>
                        <option value="Liberado">Liberado</option>
                        <option value="Canalizado">Canalizado</option>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Observaciones</FormLabel>
                      <Textarea value={form.observaciones ?? ''} onChange={e => set('observaciones', e.target.value)} isReadOnly={readOnly} rows={2} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                    </FormControl>
                  </SimpleGrid>
                </VStack>
              </TabPanel>

              {/* ── TAB 3: Evidencia y Ubicación ──────────────────────── */}
              <TabPanel px={0}>
                <VStack spacing={4} align="stretch">
                  <Text fontWeight="semibold" color="#5b182b">Ubicación de la detención (Google Maps)</Text>
                  {!readOnly ? (
                    <MapSelector
                      initialLat={form.lugarLat ?? null}
                      initialLng={form.lugarLng ?? null}
                      onLocationSelect={(lat, lng, address) => {
                        set('lugarLat', lat);
                        set('lugarLng', lng);
                        if (address?.fullAddress && !form.lugarDetencion) set('lugarDetencion', address.fullAddress);
                      }}
                    />
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      Coordenadas: {form.lugarLat ?? '—'}, {form.lugarLng ?? '—'}
                    </Text>
                  )}

                  <Divider />

                  {!detencion?.id ? (
                    <Box p={4} bg="orange.50" borderRadius="md">
                      <Text fontSize="sm" color="orange.700">
                        Las fotografías e identificación se podrán subir una vez registrada la detención. Guarde primero y vuelva a abrir en modo edición.
                      </Text>
                    </Box>
                  ) : (
                    <>
                      <input type="file" hidden ref={refFrontal} accept="image/*"
                        onChange={e => { const f = e.target.files?.[0]; if (f) subirArchivo(f, 'FOTO_FRONTAL'); e.target.value = ''; }} />
                      <input type="file" hidden ref={refPerfil} accept="image/*"
                        onChange={e => { const f = e.target.files?.[0]; if (f) subirArchivo(f, 'FOTO_PERFIL'); e.target.value = ''; }} />
                      <input type="file" hidden ref={refIdent} accept="image/*,application/pdf"
                        onChange={e => { const f = e.target.files?.[0]; if (f) subirArchivo(f, 'IDENTIFICACION'); e.target.value = ''; }} />

                      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                        <Box>
                          <Text fontWeight="semibold" fontSize="sm" mb={2}>Fotografía frontal</Text>
                          {archivosPorTipo('FOTO_FRONTAL').map(a => (
                            <Image key={a.id} src={a.url} alt="frontal" borderRadius="md" mb={2} maxH="160px" />
                          ))}
                          {!readOnly && (
                            <Button size="sm" leftIcon={<MdImage />} variant="outline" colorScheme="red" isLoading={subiendo} onClick={() => refFrontal.current?.click()}>
                              Subir frontal
                            </Button>
                          )}
                        </Box>
                        <Box>
                          <Text fontWeight="semibold" fontSize="sm" mb={2}>Fotografía de perfil</Text>
                          {archivosPorTipo('FOTO_PERFIL').map(a => (
                            <Image key={a.id} src={a.url} alt="perfil" borderRadius="md" mb={2} maxH="160px" />
                          ))}
                          {!readOnly && (
                            <Button size="sm" leftIcon={<MdImage />} variant="outline" colorScheme="red" isLoading={subiendo} onClick={() => refPerfil.current?.click()}>
                              Subir perfil
                            </Button>
                          )}
                        </Box>
                        <Box>
                          <Text fontWeight="semibold" fontSize="sm" mb={2}>Identificación (PDF/JPG)</Text>
                          {archivosPorTipo('IDENTIFICACION').map(a => (
                            <Link key={a.id} href={a.url} isExternal display="block" mb={2} color="#5b182b">
                              <HStack><MdPictureAsPdf /><Text fontSize="sm">{a.originalName ?? 'Ver archivo'}</Text></HStack>
                            </Link>
                          ))}
                          {!readOnly && (
                            <Button size="sm" leftIcon={<MdUpload />} variant="outline" colorScheme="red" isLoading={subiendo} onClick={() => refIdent.current?.click()}>
                              Subir identificación
                            </Button>
                          )}
                        </Box>
                      </SimpleGrid>
                    </>
                  )}
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>

        <ModalFooter borderTopWidth={1}>
          <Button variant="ghost" mr={3} onClick={onClose}>Cerrar</Button>
          {!readOnly && (
            <Button
              bg="#5b182b"
              color="white"
              _hover={{ bg: '#691f30' }}
              isLoading={saving}
              onClick={handleGuardar}
            >
              {mode === 'new' ? 'Registrar Detención' : 'Guardar Cambios'}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
